import dayjs from "dayjs"

import type { LeadKpiTileData } from "../LeadsActivePage/components/kpi/LeadKpiTile.tsx"
import { OPERATIONAL_STATES } from "../../Lead/constants/operational-states.ts"
import { serializeData } from "../../../utils/utils"
import * as services from "../api/services"
import type { LeadsStats } from "../api/services.types.ts"
import type { LeadsFilters } from "./leads.types.ts"

/**
 * Interim source for the KPI row while `GET /leads/stats/` is unimplemented —
 * see docs/specs/2026-08-13-leads-stats-contract.md.
 *
 * Each figure is one list query narrowed to the metric and asked for a single
 * row: the response's `count` is the aggregate, not the page. The numbers are
 * therefore real, and computed the same way the endpoint will compute them —
 * the cost is one request per figure, which is what the endpoint exists to
 * collapse.
 *
 * The figures deliberately ignore the table's filters. They are a fixed
 * overview of the whole pipeline, which is the frame of reference you filter
 * *against* — numbers that dissolve as you narrow are no use as a baseline,
 * and the design shows Active Leads at 243 while a 124-lead chip is selected.
 *
 * Two things are deliberately absent rather than approximated:
 *
 * - **Trend deltas.** "vs last 7 days" needs history the list endpoint cannot
 *   answer for. `KpiCard` reads a zero delta as "flat", which is a claim, so
 *   the compare block is omitted instead.
 * - **Closed-Lost (Month).** It counts leads that *transitioned* to Lost within
 *   the month, and no filter exposes the transition date — `created_*` would
 *   silently answer a different question. It waits for the endpoint.
 */

const today = () => dayjs().format("YYYY-MM-DD")

// The multi-value filters match with `__overlap` against a comma-joined
// param, and nulls have to be dropped rather than sent empty — both are
// `serializeData`'s job, so a derived query has to go through it exactly as the
// table's own request does. Skipping it sends `operational_states` as a
// repeated param the backend ignores, and every tile silently reports the
// unfiltered total.
const countFor = async (
  params: Partial<LeadsFilters>
): Promise<number | null> => {
  const response = await services.getLeads(
    serializeData({
      ...params,
      view: "active",
      page: 1,
      page_size: 1
    }) as Partial<LeadsFilters>
  )

  if (!response.ok) return null
  return (response.data as { count?: number })?.count ?? null
}

interface DerivedTileSpec {
  id: string
  title: string
  icon: LeadKpiTileData["icon"]
  /** The design gives each metric its own glyph colour. */
  tint: string
}

const TILE_SPECS: DerivedTileSpec[] = [
  {
    id: "active_leads",
    title: "Active Leads",
    icon: "people",
    tint: "#3B82F6"
  },
  { id: "hot_leads", title: "Hot Leads", icon: "flame", tint: "#EF4444" },
  {
    id: "follow_ups_today",
    title: "Follow Ups Today",
    icon: "clock",
    tint: "#F59E0B"
  }
]

/**
 * A KPI URL has an explicit marker even when the tile has no extra filters.
 * The marker tells the page to reset persisted filters, so the Active Leads
 * tile remains a real drill-through rather than a no-op on `/leads`.
 */
const drillTo = (id: string, params: Partial<LeadsFilters>): string => {
  const search = new URLSearchParams()
  search.set("kpi", id)

  // Use the same serialisation as the API request. This keeps the destination
  // aligned with the scoped count when a user has already narrowed the table.
  const {
    page: _page,
    page_size: _pageSize,
    view: _view,
    reason: _reason,
    ...queryFilters
  } = params
  const serialized = serializeData(queryFilters)
  Object.entries(serialized).forEach(([key, value]) => {
    if (value != null) search.set(key, String(value))
  })

  const query = search.toString()
  return query ? `/leads?${query}` : "/leads"
}

export async function deriveLeadsStats({
  userId
}: { userId?: string | null } = {}): Promise<LeadsStats> {
  const day = today()

  const [active, hot, followUps, myActive, hotFollowUp, newToday] =
    await Promise.all([
      countFor({}),
      countFor({ operational_states: [OPERATIONAL_STATES.HOT] }),
      // Due today *or* overdue, matching the endpoint this stands in for.
      // An exact match on today drops yesterday's missed follow-up, the one
      // the tile most needs to surface.
      countFor({ follow_up_date_before: day }),
      userId ? countFor({ sales_manager: [userId] }) : Promise.resolve(null),
      countFor({
        operational_states: [
          OPERATIONAL_STATES.HOT,
          OPERATIONAL_STATES.FOLLOW_UP
        ]
      }),
      countFor({ created_after: day })
    ])

  const values: Record<string, number | null> = {
    active_leads: active,
    hot_leads: hot,
    follow_ups_today: followUps
  }

  const tiles: LeadKpiTileData[] = TILE_SPECS.map((spec) => ({
    id: spec.id,
    title: spec.title,
    icon: spec.icon,
    tint: spec.tint,
    value: values[spec.id] ?? "—",
    // The tiles count the whole pipeline, so a drill-through carries only the
    // tile's own narrowing — there is no surrounding filter set to preserve.
    drillTo:
      spec.id === "hot_leads"
        ? drillTo(spec.id, {
            operational_states: [OPERATIONAL_STATES.HOT]
          })
        : spec.id === "follow_ups_today"
          ? drillTo(spec.id, { follow_up_date_before: day })
          : drillTo(spec.id, {})
  }))

  const smartFilters: Record<string, number> = {}
  if (myActive !== null) smartFilters.my_active_leads = myActive
  if (hotFollowUp !== null) smartFilters.hot_follow_up = hotFollowUp
  if (newToday !== null) smartFilters.new_today = newToday

  return { tiles, smart_filters: smartFilters }
}
